import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Boxes, IndianRupee, Layers, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  DataTable,
  KpiCard,
  Panel,
  inr,
  num,
  type DataColumn,
} from "@/components/admin/analytics/AnalyticsKit";
import { FieldGroup, NumberField, SwitchField, TextField } from "@/components/admin/delivery/form-bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  deleteInventoryCategory,
  listInventoryCategories,
  saveInventoryCategory,
} from "@/lib/admin/inventory.functions";
import {
  categorySchema,
  type CategoryInput,
  type InventoryCategory,
} from "@/lib/admin/inventory.schemas";

export const Route = createFileRoute("/_admin/admin/inventory-categories")({
  head: () => ({
    meta: [
      { title: "Inventory Categories — Admin" },
      {
        name: "description",
        content: "Kitchen store categories used to group every ingredient and consumable.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: InventoryCategoriesPage,
});

function emptyForm(): CategoryInput {
  return { name: "", description: "", sort_order: 0, is_active: true };
}

function InventoryCategoriesPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listInventoryCategories);
  const saveFn = useServerFn(saveInventoryCategory);
  const deleteFn = useServerFn(deleteInventoryCategory);

  const categories = useQuery({
    queryKey: ["admin", "inventory", "categories"],
    queryFn: () => listFn(),
    staleTime: 15_000,
  });

  const [editing, setEditing] = useState<InventoryCategory | "new" | null>(null);
  const [form, setForm] = useState<CategoryInput>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<InventoryCategory | null>(null);

  useEffect(() => {
    if (editing === "new") setForm(emptyForm());
    else if (editing)
      setForm({
        name: editing.name,
        description: editing.description,
        sort_order: editing.sort_order,
        is_active: editing.is_active,
      });
    setErrors({});
  }, [editing]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "inventory"] });

  const save = useMutation({
    mutationFn: (v: { id: string | null; patch: CategoryInput }) => saveFn({ data: v as never }),
    onSuccess: async () => {
      toast.success("Category saved");
      setEditing(null);
      await invalidate();
    },
    onError: (e: Error) =>
      toast.error(e.message === "duplicate" ? "That category already exists." : "Couldn't save."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Category removed");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: () => toast.error("Couldn't remove category."),
  });

  const rows = useMemo(() => categories.data ?? [], [categories.data]);
  const kpi = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((r) => r.is_active).length,
      items: rows.reduce((s, r) => s + r.item_count, 0),
      value: rows.reduce((s, r) => s + r.stock_value_paise, 0),
    }),
    [rows],
  );

  const submit = () => {
    const parsed = categorySchema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] = issue.message;
      setErrors(next);
      return;
    }
    save.mutate({ id: editing === "new" || !editing ? null : editing.id, patch: parsed.data });
  };

  const columns: Array<DataColumn<InventoryCategory>> = [
    {
      key: "name",
      header: "Category",
      render: (r) => (
        <div>
          <p className="font-medium text-foreground">{r.name}</p>
          <p className="text-[11px] text-muted-foreground">{r.description || "—"}</p>
        </div>
      ),
      sortValue: (r) => r.name,
      exportValue: (r) => r.name,
    },
    {
      key: "items",
      header: "Items",
      align: "right",
      render: (r) => num(r.item_count),
      sortValue: (r) => r.item_count,
      exportValue: (r) => r.item_count,
    },
    {
      key: "value",
      header: "Stock value",
      align: "right",
      render: (r) => inr(r.stock_value_paise / 100),
      sortValue: (r) => r.stock_value_paise,
      exportValue: (r) => r.stock_value_paise / 100,
    },
    {
      key: "order",
      header: "Sort",
      align: "right",
      render: (r) => num(r.sort_order),
      sortValue: (r) => r.sort_order,
      exportValue: (r) => r.sort_order,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge variant={r.is_active ? "secondary" : "outline"}>
          {r.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
      exportValue: (r) => (r.is_active ? "Active" : "Inactive"),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(r)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-destructive"
            onClick={() => setConfirmDelete(r)}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Inventory Categories"
        description="Group every ingredient and consumable so stock, purchasing and analytics stay organised."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Categories"
          value={num(kpi.total)}
          tone="info"
          icon={Layers}
          loading={categories.isPending}
        />
        <KpiCard
          label="Active"
          value={num(kpi.active)}
          tone="success"
          icon={Layers}
          loading={categories.isPending}
        />
        <KpiCard
          label="Items tracked"
          value={num(kpi.items)}
          tone="teal"
          icon={Boxes}
          loading={categories.isPending}
        />
        <KpiCard
          label="Stock value"
          value={inr(kpi.value / 100)}
          tone="purple"
          icon={IndianRupee}
          loading={categories.isPending}
        />
      </div>

      <Panel title="All categories" subtitle={`${rows.length} total`}>
        <div className="mb-3 flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setEditing("new")}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New category
          </Button>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={categories.isPending}
          exportName="inventory-categories"
          importModule="inventory-categories"
          pageSize={12}
          minWidth={700}
          searchOn={(r) => `${r.name} ${r.description}`}
        />
      </Panel>

      <Dialog open={editing !== null} onOpenChange={(o) => (o ? null : setEditing(null))}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New category" : "Edit category"}</DialogTitle>
          </DialogHeader>
          <FieldGroup title="Category details" cols={2}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              error={errors["name"]}
            />
            <NumberField
              label="Sort order"
              value={form.sort_order}
              onChange={(v) => setForm({ ...form, sort_order: v })}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(v) => setForm({ ...form, description: v })}
              className="sm:col-span-2"
            />
            <div className="sm:col-span-2">
              <SwitchField
                label="Active"
                hint="Inactive categories stay in reports but are hidden when adding new items."
                checked={form.is_active}
                onChange={(v) => setForm({ ...form, is_active: v })}
              />
            </div>
          </FieldGroup>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => (o ? null : setConfirmDelete(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Items in this category stay in stock but become uncategorised.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
